import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import useAppModal from 'hooks/useAppModal';
import useAppMutation from 'hooks/useAppMutation';
import useAppUser from 'hooks/useAppUser';
import useNotify from 'hooks/useNotify';
import { modalIds } from 'components/modal/Modal.constants';
import IntegrationsAPI from 'services/IntegrationsAPI';
import { ScreenAndProtectEnrollmentResponse } from 'models/ScreenAndProtect';
import { SCREEN_AND_PROTECT_ENROLLMENT_BASE_QUERY_KEY } from '../../useFetchScreenAndProtectEnrollment';
import CancelProtectionModalBody from './CancelProtectionModalBody';

const useCancelProtectionModal = () => {
  const { agencyUid } = useAppUser();
  const { t } = useTranslation();
  const { openConfirmModal, closeModal, setErrorConfirmModal } = useAppModal();
  const { notifySuccess } = useNotify();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const closeConfirmationModal = () => {
    closeModal(modalIds.confirm);
  };

  const { mutateAsync } = useAppMutation(
    () => {
      return IntegrationsAPI.delete(
        `/api/internal/screen-and-protect/enrollment/${agencyUid}`,
      );
    },
    {
      onSuccess: () => {
        notifySuccess(t('pageScreenAndProtect.cancellation.cancelSuccess'));
        closeConfirmationModal();
        // perform cache update synchronously before navigating. using invalidateQueries would leave
        // stale isActive:true data in the cache, causing incorrect redirects during the async refetch.
        queryClient.setQueryData<ScreenAndProtectEnrollmentResponse>(
          [SCREEN_AND_PROTECT_ENROLLMENT_BASE_QUERY_KEY, agencyUid],
          (oldData) => {
            if (!oldData) {
              return oldData;
            }
            const newData = { ...oldData, isActive: false };
            return newData;
          },
        );
        navigate('/screen-and-protect');
      },
      onError: (error: any) => {
        setErrorConfirmModal(
          error?.response?.data?.apiErrorMessage ||
            t('pageScreenAndProtect.cancellation.cancelError'),
        );
      },
    },
  );

  const handleCancelProtection = () =>
    new Promise<void>((resolve) => {
      mutateAsync().then(() => resolve());
    });

  const openCancelProtectionModal = () => {
    openConfirmModal({
      title: t('pageScreenAndProtect.currentPage.cancelConfirmation.title'),
      body: <CancelProtectionModalBody />,
      alertMessage: t(
        'pageScreenAndProtect.currentPage.cancelConfirmation.alertMessage',
      ),
      confirmColor: 'danger',
      confirmLabel: t(
        'pageScreenAndProtect.currentPage.cancelConfirmation.confirmLabel',
      ),
      cancelLabel: t(
        'pageScreenAndProtect.currentPage.cancelConfirmation.cancelLabel',
      ),
      onConfirmAsync: handleCancelProtection,
    });
  };

  return { openCancelProtectionModal };
};

export default useCancelProtectionModal;
